// Per-request PUT timeout, scaled to the file. A flat 5 minutes was wrong in
// both directions: a dead socket on a 200 KB thumbnail sat there for five
// minutes before the first retry even started (so six attempts could not
// finish inside any realistic session), while a genuinely slow 20 MB photo on
// a weak mobile link could still be cut off mid-flight.
//
// The numbers below are measured, not guessed. Over 21,463 real PUTs to MinIO
// in 24h (traefik access logs, duration = time to serve the whole request):
//
//   p50      761ms
//   p90    7,555ms
//   p95   12,760ms
//   p99   44,655ms   <- ABOVE the old 30s floor
//   p99.9 107,490ms
//   max   271,887ms
//
// So roughly 1 upload in 100 needed longer than the old floor allowed, and we
// cut it off — 361 PUTs exceeded 30s, 117 exceeded 60s. Those show up in
// Sentry as ERR_NETWORK with status 0 (the request never completed), which is
// indistinguishable from a dead connection, and they are why a customer could
// lose a third of a 211-photo batch.
//
// This is NOT the server struggling: during those failures MinIO sat at ~16%
// CPU and 4GB of 47GB, the host at 93% idle with 0% iowait, no restarts and no
// errors logged. The slow requests are slow CLIENT uploads — a phone pushing
// megabytes over mobile data — and the old 64 KB/s assumption was simply too
// optimistic for the worst connections we actually serve.
//
// Floor covers p99 with margin; the per-byte allowance is halved to 32 KB/s so
// large photos on weak links get real headroom. The ceiling is unchanged
// because nothing in 24h came close to it (max 272s, zero over 300s).
const UPLOAD_TIMEOUT_FLOOR_MS = 60_000;
const UPLOAD_TIMEOUT_CEILING_MS = 300_000;
const UPLOAD_MIN_THROUGHPUT_BYTES_PER_S = 32 * 1024;

export function uploadTimeoutMs(sizeBytes: number): number {
  const payloadMs =
    (Math.max(0, sizeBytes) / UPLOAD_MIN_THROUGHPUT_BYTES_PER_S) * 1000;
  return Math.min(
    UPLOAD_TIMEOUT_CEILING_MS,
    Math.round(UPLOAD_TIMEOUT_FLOOR_MS + payloadMs)
  );
}

/**
 * Total wall clock one file may consume across ALL its retries.
 *
 * Two and a half attempts' worth. Enough that a genuinely slow upload gets a
 * second and third go, while a file going nowhere stops burning the customer's
 * session — without this, a per-attempt timeout generous enough for the p99
 * multiplies by six and strands them for minutes.
 */
export function uploadTotalBudgetMs(sizeBytes: number): number {
  return Math.round(uploadTimeoutMs(sizeBytes) * 2.5);
}
